import { ReactElement, useCallback, useMemo, useState } from 'react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { useHotkeys } from 'react-hotkeys-hook'
import { Vector3 } from 'three'
import { Close } from '@mui/icons-material'
import { IconButton, Paper, Stack, TextField, Typography } from '@mui/material'
import { ThemeProvider, useTheme } from '@mui/material/styles'
import CommentSettingsFields from '@editor/components/CommentDrawer/CommentSettingsFields'
import LinkedCommentInfo from '@editor/components/CommentDrawer/LinkedCommentInfo'
import {
  CommentSettingsFormValues,
  commentToFormValues,
  formValuesToEditedFields,
} from '@editor/components/CommentDrawer/commentSettingsForm'
import { Html } from '@react-three/drei'
import { useCanvasEvent, useElementLabel, useJumpToResultElement } from '@editorHooks'
import Comment3D from '../Comment3D'
import useCommentMutations from './useCommentMutations'

interface Props {
  comment: Comment3D
  labelScale: number
  onClose: () => void
}

/**
 * Minimal in-scene editor for a comment: text, type (Notiz / Aussteifungsplan / Positionsplan /
 * Custom) and the storeys the comment is shown on in plans. The settings form (everything but the
 * text) is shared with the CommentDrawer (see `CommentSettingsFields`); this popup adds the text
 * field plus a live scene preview and saves the edits when it closes (canvas click / Escape / close
 * button).
 *
 * Unlike the drawer (which persists each change via Form2's `submitOnChange`), the popup only
 * provides the RHF context and defers persistence to close — so it uses a plain `FormProvider`
 * rather than Form2. The form is created here (in the r3f tree) so the preview can read its values,
 * but the `FormProvider` is placed inside the drei `<Html>` because context doesn't cross the
 * r3f → DOM portal boundary (same reason the MUI theme is re-provided there).
 */
const CommentEditPopup = ({ comment, labelScale, onClose }: Props): ReactElement => {
  const { updateCommentFields } = useCommentMutations()
  const jumpToResultElement = useJumpToResultElement()
  const getLabel = useElementLabel()
  // The drei <Html> portal renders outside the r3f tree, so the app's MUI theme context doesn't
  // reach it. Re-provide it from the theme available here (under Scene's ThemeProvider).
  const theme = useTheme()

  const commentId = comment.id
  // Element-linked comments: their text and type are owned by the position list, so both are
  // locked here. They gain a "jump to element" action instead.
  const linkedElementGuid = comment.element_guid ?? null
  const isLinked = !!linkedElementGuid

  const methods = useForm<CommentSettingsFormValues>({
    defaultValues: commentToFormValues(comment),
  })

  // Live form values drive the preview below; persistence happens only on save-and-close. Read via
  // an explicit `control` so this works outside the `FormProvider` (which lives inside <Html>).
  const values = useWatch({ control: methods.control })
  const editedFields = useMemo(
    () => formValuesToEditedFields(values as CommentSettingsFormValues),
    [values],
  )

  // The text is edited here rather than in the shared settings form (the CommentDrawer doesn't
  // expose it). The other fields come from `editedFields`.
  const [text, setText] = useState<string>(comment.text)

  const anchorPosition = useMemo(
    () => new Vector3(comment.position.x, comment.position.y, comment.position.z),
    [comment.position],
  )

  // Live preview rendered by this popup (see the <Comment3D> below) instead of mutating the comments
  // store, so the scene shows the in-progress edits and nothing has to be reverted when the popup
  // closes without saving.
  const previewComment = useMemo<Comment3D>(
    () => ({ ...comment, ...editedFields, text }),
    [comment, editedFields, text],
  )

  const handleSaveAndClose = useCallback(() => {
    // Don't persist an empty/whitespace-only label — keep the prior text in that case.
    const textField = text.trim().length > 0 ? { text } : {}
    updateCommentFields(commentId, {
      ...formValuesToEditedFields(methods.getValues()),
      ...textField,
    })
    onClose()
  }, [commentId, text, updateCommentFields, onClose, methods])

  // Persist any edits, close the popup, then select the linked element in Results2 (navigating
  // there from whichever view we're in). Created here rather than in LinkedCommentInfo because
  // the router context the jump needs doesn't cross the drei <Html> portal below.
  const handleJumpToElement = useCallback(() => {
    handleSaveAndClose()
    if (linkedElementGuid) jumpToResultElement(linkedElementGuid)
  }, [handleSaveAndClose, jumpToResultElement, linkedElementGuid])

  // A left-button pointer-down on the scene canvas saves the edits and closes the popup. Listening on
  // the canvas (not window) means clicks on other UI – including the popup's own MUI dropdown menus
  // (body portals) – don't close it. Non-left buttons are ignored so a right-button camera drag
  // keeps the popup open.
  const handleCanvasPointerDown = useCallback(
    (event: PointerEvent) => {
      if (event.button !== 0) return
      handleSaveAndClose()
    },
    [handleSaveAndClose],
  )
  useCanvasEvent('pointerdown', handleCanvasPointerDown)

  // Escape also saves and closes.
  useHotkeys(
    'esc',
    event => {
      event.stopPropagation()
      handleSaveAndClose()
    },
    { enableOnFormTags: true },
    [handleSaveAndClose],
  )

  return (
    <>
      {/* Live preview of the in-progress edits (the controller hides the real comment while it is
          being edited). The interactive callout overlay renders the callouts, so they are skipped
          here. */}
      <Comment3D comment={previewComment} labelScale={labelScale} hideCallouts />

      <Html position={anchorPosition} zIndexRange={[100, 0]} style={{ pointerEvents: 'auto' }}>
        <ThemeProvider theme={theme}>
          <FormProvider {...methods}>
            <Paper
              elevation={4}
              sx={{ p: 1.5, width: 280, transform: 'translate(20px, -50%)' }}
              onPointerDown={event => event.stopPropagation()}
              data-cy="comment-edit-popup"
            >
              <Stack spacing={1.5}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="subtitle2">Kommentar bearbeiten</Typography>
                  <IconButton
                    size="small"
                    onClick={handleSaveAndClose}
                    sx={{ mr: -0.5, mt: -0.5 }}
                    data-cy="comment-popup-close"
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Stack>

                {linkedElementGuid && (
                  <LinkedCommentInfo
                    label={getLabel(linkedElementGuid)}
                    onJump={handleJumpToElement}
                  />
                )}

                <Stack spacing={0.25}>
                  <Typography variant="caption" color="text.secondary">
                    Text
                  </Typography>
                  <TextField
                    // Uncontrolled: a controlled value gets re-applied by drei's frequent <Html>
                    // re-renders (driven here by the live preview), which resets the caret to the
                    // end while typing in the middle. We mirror changes into `text` for the
                    // preview/commit only.
                    defaultValue={comment.text}
                    size="small"
                    fullWidth
                    multiline
                    minRows={1}
                    maxRows={6}
                    disabled={isLinked}
                    onChange={event => setText(event.target.value)}
                    data-cy="comment-popup-text"
                  />
                </Stack>

                <CommentSettingsFields
                  allowCustomType={comment.type === 'custom'}
                  disableType={isLinked}
                />
              </Stack>
            </Paper>
          </FormProvider>
        </ThemeProvider>
      </Html>
    </>
  )
}

export default CommentEditPopup
